class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('change', this.onAvailabilityFilterChange.bind(this), true);

    if (facetForm.dataset.stagedFacets === 'true') {
      this.initStagedDrawer(facetForm);
    } else {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static availabilityPreferenceStorageKey() {
    return 'farfie:availability-preference';
  }

  static getAvailabilityPreference() {
    try {
      return sessionStorage.getItem(FacetFiltersForm.availabilityPreferenceStorageKey()) || FacetFiltersForm.availabilityPreferenceFallback;
    } catch (error) {
      return FacetFiltersForm.availabilityPreferenceFallback;
    }
  }

  static setAvailabilityPreference(preference) {
    FacetFiltersForm.availabilityPreferenceFallback = preference;

    try {
      sessionStorage.setItem(FacetFiltersForm.availabilityPreferenceStorageKey(), preference);
    } catch (error) {
      // Keep the in-memory preference for the current page if storage is blocked.
    }
  }

  static disableDefaultAvailability() {
    FacetFiltersForm.setAvailabilityPreference('custom');
  }

  static isDefaultAvailabilityEnabled() {
    return FacetFiltersForm.getAvailabilityPreference() !== 'custom';
  }

  static hasAvailabilityParam(searchParams = '') {
    return new URLSearchParams(searchParams).has('filter.v.availability');
  }

  static shouldApplyDefaultAvailability(searchParams = window.location.search.slice(1)) {
    return FacetFiltersForm.isDefaultAvailabilityEnabled() && !FacetFiltersForm.hasAvailabilityParam(searchParams);
  }

  static applyDefaultAvailabilityToSearchParams(searchParams = '', root = document) {
    const params = new URLSearchParams(searchParams);
    const hasAvailabilityControl = root?.querySelector?.('input[name="filter.v.availability"]');

    if (hasAvailabilityControl && !params.has('filter.v.availability') && FacetFiltersForm.isDefaultAvailabilityEnabled()) {
      params.append('filter.v.availability', '1');
    }

    return params.toString();
  }

  static replaceURLHash(searchParams) {
    history.replaceState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static enforceDefaultAvailabilityInputs(root = document) {
    const shouldApplyDefault = FacetFiltersForm.shouldApplyDefaultAvailability();

    root.querySelectorAll('input[name="filter.v.availability"]').forEach((input) => {
      if (shouldApplyDefault) input.checked = input.value === '1';
    });
  }

  static syncDefaultAvailabilityFacets(root = document) {
    const hideDefaultAvailabilityFacet = FacetFiltersForm.isDefaultAvailabilityEnabled();

    root.querySelectorAll('[data-default-availability-facet]').forEach((facet) => {
      facet.hidden = hideDefaultAvailabilityFacet;
    });

    document.querySelectorAll('.active-facets').forEach((container) => {
      const visibleFacets = Array.from(container.querySelectorAll('facet-remove:not(.active-facets__button-wrapper)'))
        .filter((facet) => !facet.hidden);

      container.querySelectorAll('.active-facets__button-wrapper').forEach((clearAllWrapper) => {
        clearAllWrapper.hidden = visibleFacets.length === 0;
      });
    });
  }

  static syncDefaultAvailabilityUI(root = document) {
    FacetFiltersForm.enforceDefaultAvailabilityInputs(root);
    FacetFiltersForm.syncDefaultAvailabilityFacets(root);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
      FacetFiltersForm.renderSectionFromFetch(url, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
    if (window.initCollectionLoadMoreButton) window.initCollectionLoadMoreButton();
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.syncDefaultAvailabilityFacets();
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const mobileDrawer = mobileForm ? mobileForm.closest('menu-drawer') : null;
    if (mobileDrawer) mobileDrawer.bindEvents();
    const mobileFacetForm = mobileForm ? mobileForm.closest('facet-filters-form') : null;
    if (mobileFacetForm && mobileFacetForm.initStagedDrawer) mobileFacetForm.initStagedDrawer(mobileForm);
    FacetFiltersForm.syncDefaultAvailabilityUI();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid') || document.getElementById('Huratips-Loop');

    if (!productGrid) return [];

    return [
      {
        section: productGrid.dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams();

    formData.forEach((value, key) => {
      if (value === '') return;
      searchParams.append(key, value);
    });

    return FacetFiltersForm.applyDefaultAvailabilityToSearchParams(searchParams.toString(), form);
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  initStagedDrawer(form) {
    if (!form || form.dataset.stagedFacetsInitialized === 'true') {
      this.bindStagedDrawerInputGuards(form);
      this.bindDrawerAccordionSummaries(form);
      this.bindPriceSliders(form);
      this.syncAllPriceSliders(form);
      this.normalizeDrawerAccordion(form);
      return;
    }

    form.dataset.stagedFacetsInitialized = 'true';
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (event.submitter?.matches('[data-facets-apply]')) {
        this.applyStagedDrawer(form, event);
      }
    });

    this.bindStagedDrawerInputGuards(form);

    form.querySelector('[data-facets-apply]')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.applyStagedDrawer(form, event);
    }, true);

    form.querySelector('[data-facets-clear]')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.clearStagedDrawer(form);
    }, true);

    form.querySelector('[data-facets-close]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.closeStagedDrawer(form);
    });

    form.querySelector('[data-facets-close]')?.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() !== 'SPACE' && event.code.toUpperCase() !== 'ENTER') return;
      event.preventDefault();
      this.closeStagedDrawer(form);
    });

    form.addEventListener('click', (event) => {
      if (event.target !== form) return;
      event.preventDefault();
      this.closeStagedDrawer(form);
    });

    this.bindDrawerAccordionSummaries(form);
    this.bindPriceSliders(form);
    this.syncAllPriceSliders(form);
    this.normalizeDrawerAccordion(form);
  }

  bindStagedDrawerInputGuards(form) {
    if (!form || form.dataset.stagedFacetsInputGuardsBound === 'true') return;

    form.dataset.stagedFacetsInputGuardsBound = 'true';
    const drawerControlSelector = '.facets-drawer__section, .facets-drawer__submenu, .facets-drawer__option-label, .facets-drawer__radio';
    const drawerActionSelector = '[data-facets-apply], [data-facets-clear], [data-facets-close], .mobile-facets__summary';

    ['input', 'change'].forEach((eventName) => {
      form.addEventListener(eventName, (event) => {
        if (!event.target.closest('input, select, textarea')) return;
        event.stopPropagation();
      });
    });

    ['pointerdown', 'mousedown', 'touchstart', 'click'].forEach((eventName) => {
      form.addEventListener(eventName, (event) => {
        if (event.target.closest(drawerActionSelector)) return;
        if (!event.target.closest(drawerControlSelector)) return;
        event.stopPropagation();
      }, true);
    });
  }

  bindPriceSliders(form) {
    if (!form) return;

    form.querySelectorAll('[data-price-slider]').forEach((slider) => {
      if (slider.dataset.priceSliderBound === 'true') return;
      slider.dataset.priceSliderBound = 'true';
      slider.querySelectorAll('[data-price-min], [data-price-max]').forEach((input) => {
        input.addEventListener('input', () => this.syncPriceSlider(slider));
        input.addEventListener('change', () => this.syncPriceSlider(slider));
      });
      const priceFields = slider.querySelector('.facets-drawer__price-fields');
      priceFields?.querySelectorAll('.field__input').forEach((input) => {
        input.addEventListener('input', () => this.syncPriceFieldsToSlider(slider, input));
        input.addEventListener('change', () => this.syncPriceFieldsToSlider(slider, input));
      });
    });
  }

  bindDrawerAccordionSummaries(form) {
    if (!form) return;

    form.querySelectorAll('.mobile-facets__details > .mobile-facets__summary').forEach((summary) => {
      if (summary.dataset.drawerAccordionBound === 'true') return;
      summary.dataset.drawerAccordionBound = 'true';
      summary.addEventListener('click', (event) => {
        event.stopImmediatePropagation();
        const currentDetails = summary.closest('.mobile-facets__details');
        if (!currentDetails) return;

        form.querySelectorAll('.mobile-facets__details[open]').forEach((details) => {
          if (details !== currentDetails) details.removeAttribute('open');
        });
      }, true);
    });
  }

  normalizeDrawerAccordion(form) {
    if (!form) return;

    let firstOpenDetails = null;
    form.querySelectorAll('.mobile-facets__details[open]').forEach((details) => {
      if (!firstOpenDetails) {
        firstOpenDetails = details;
        return;
      }

      details.removeAttribute('open');
    });
  }

  syncAllPriceSliders(form) {
    if (!form) return;
    form.querySelectorAll('[data-price-slider]').forEach((slider) => this.syncPriceSlider(slider));
  }

  syncPriceSlider(slider) {
    const minRange = slider.querySelector('[data-price-min]');
    const maxRange = slider.querySelector('[data-price-max]');
    const priceFields = slider.querySelector('.facets-drawer__price-fields');
    const priceInputs = priceFields ? priceFields.querySelectorAll('.field__input') : [];
    const minField = priceInputs[0];
    const maxField = priceInputs[1];
    const rangeMax = Number(slider.dataset.rangeMax || maxRange?.max || 0);

    if (!minRange || !maxRange) return;

    let minValue = Number(minRange.value || 0);
    let maxValue = Number(maxRange.value || rangeMax);

    if (minValue > maxValue) {
      if (document.activeElement === minRange) {
        minValue = maxValue;
        minRange.value = String(minValue);
      } else {
        maxValue = minValue;
        maxRange.value = String(maxValue);
      }
    }

    if (minField) minField.value = minValue > 0 ? String(minValue) : '';
    if (maxField) maxField.value = maxValue < rangeMax ? String(maxValue) : '';
  }

  syncPriceFieldsToSlider(slider, activeField) {
    const minRange = slider.querySelector('[data-price-min]');
    const maxRange = slider.querySelector('[data-price-max]');
    const priceFields = slider.querySelector('.facets-drawer__price-fields');
    const priceInputs = priceFields ? priceFields.querySelectorAll('.field__input') : [];
    const minField = priceInputs[0];
    const maxField = priceInputs[1];
    const rangeMax = Number(slider.dataset.rangeMax || maxRange?.max || 0);

    if (!minRange || !maxRange || !minField || !maxField) return;

    const rawMinValue = minField.value.trim();
    const rawMaxValue = maxField.value.trim();
    let minValue = rawMinValue === '' ? 0 : Number(rawMinValue);
    let maxValue = rawMaxValue === '' ? rangeMax : Number(rawMaxValue);

    if (!Number.isFinite(minValue)) minValue = 0;
    if (!Number.isFinite(maxValue)) maxValue = rangeMax;

    minValue = Math.max(0, Math.min(minValue, rangeMax));
    maxValue = Math.max(0, Math.min(maxValue, rangeMax));

    if (minValue > maxValue) {
      if (activeField === minField) {
        minValue = maxValue;
      } else {
        maxValue = minValue;
      }
    }

    minRange.value = String(minValue);
    maxRange.value = String(maxValue);
    minField.value = minValue > 0 ? String(minValue) : '';
    maxField.value = maxValue < rangeMax ? String(maxValue) : '';
  }

  clearStagedDrawer(form) {
    form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      input.checked = false;
    });
    FacetFiltersForm.disableDefaultAvailability();
    FacetFiltersForm.enforceDefaultAvailabilityInputs(form);
    FacetFiltersForm.syncDefaultAvailabilityFacets();

    form.querySelectorAll('.field__input').forEach((input) => {
      input.value = '';
    });

    form.querySelectorAll('[data-price-slider]').forEach((slider) => {
      const rangeMax = Number(slider.dataset.rangeMax || 0);
      const minRange = slider.querySelector('[data-price-min]');
      const maxRange = slider.querySelector('[data-price-max]');
      if (minRange) minRange.value = '0';
      if (maxRange) maxRange.value = String(rangeMax);
      this.syncPriceSlider(slider);
    });
  }

  applyStagedDrawer(form, event) {
    const searchParams = this.createSearchParams(form);
    this.onSubmitForm(searchParams, event);
    this.closeStagedDrawer(form);
  }

  closeStagedDrawer(form) {
    const summary = form.closest('.mobile-facets__wrapper')?.querySelector('.mobile-facets__open-wrapper');
    if (summary) summary.click();
  }

  onAvailabilityFilterChange(event) {
    if (!event.target.matches('input[name="filter.v.availability"]')) return;
    FacetFiltersForm.disableDefaultAvailability();
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const eventForm = event.target.closest('form');
    if (eventForm?.dataset.stagedFacets === 'true') return;

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const clickedFacet = event.currentTarget.closest('facet-remove');
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    const removesAvailability = FacetFiltersForm.hasAvailabilityParam(window.location.search.slice(1)) && !FacetFiltersForm.hasAvailabilityParam(url);

    if (removesAvailability || clickedFacet?.matches('[data-availability-facet], .active-facets__button-wrapper')) {
      FacetFiltersForm.disableDefaultAvailability();
    }

    FacetFiltersForm.toggleActiveFacets();
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.availabilityPreferenceFallback = 'default';
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = FacetFiltersForm.searchParamsInitial;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.syncDefaultAvailabilityUI();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
